import {
	Querybuildertypesv5BucketOptionsDTO,
	Querybuildertypesv5QueryTypeDTO,
} from 'api/generated/services/sigNoz.schemas';
import { TelemetryFieldKey } from 'api/v5/v5';
import { Format } from 'constants/formats/types';
import { EQueryType } from 'types/common/dashboard';
import {
	DataSource,
	QueryBuilderData,
	ReduceOperators,
} from 'types/common/queryBuilder';

import {
	BuilderQueryType,
	Filter,
	Having as HavingV5,
	LogAggregation,
	MetricAggregation,
	QueryFunction,
	TraceAggregation,
} from '../v5/queryRange';
import { BaseAutocompleteData } from './queryAutocompleteResponse';

export const QueryMode = {
	...EQueryType,
	/** Not an `EQueryType`: AI queries are builder queries tagged `builderQueryType: builder_ai_query`. */
	AI_QUERY_BUILDER: Querybuildertypesv5QueryTypeDTO.builder_ai_query,
} as const;

export type QueryMode = (typeof QueryMode)[keyof typeof QueryMode];

// Type for Formula
export interface IBuilderFormula {
	expression: string;
	disabled: boolean;
	queryName: string;
	legend: string;
	limit?: number | null;
	having?: Having[];
	stepInterval?: number;
	orderBy?: OrderByPayload[];
	/** Heatmap only. */
	bucketOptions?: Querybuildertypesv5BucketOptionsDTO;
}

export type IBuilderTraceOperator = IBuilderQuery;

export interface TagFilterItem {
	id: string;
	key?: BaseAutocompleteData;
	op: string;
	value: (string | number | boolean)[] | string | number | boolean;
}

export interface TagFilter {
	items: TagFilterItem[];
	op: string;
}

export type Having = {
	columnName: string;
	op: string;
	value: number | number[];
};

export type HavingForm = Omit<Having, 'value'> & {
	value: string[];
};

export type OrderByPayload = {
	columnName: string;
	order: string;
};

export interface QueryFunctionProps {
	name: string;
	args: (string | number)[];
	namedArgs?: Record<string, any>;
}

// Type for query builder
export type IBuilderQuery = {
	queryName: string;
	dataSource: DataSource;
	aggregateOperator?: string;
	aggregateAttribute?: BaseAutocompleteData;
	aggregations?: TraceAggregation[] | LogAggregation[] | MetricAggregation[];
	timeAggregation?: string;
	spaceAggregation?: string;
	temporality?: string;
	functions: QueryFunction[];
	filter?: Filter;
	filters?: TagFilter;
	groupBy: BaseAutocompleteData[];
	expression: string;
	disabled: boolean;
	having: Having[] | HavingV5;
	limit: number | null;
	stepInterval: number | undefined | null;
	orderBy: OrderByPayload[];
	reduceTo?: ReduceOperators;
	legend: string;
	pageSize?: number;
	offset?: number;
	selectColumns?: BaseAutocompleteData[] | TelemetryFieldKey[];
	source?: 'meter' | '';
	builderQueryType?: BuilderQueryType;
	/** Heatmap only. */
	bucketOptions?: Querybuildertypesv5BucketOptionsDTO;
};

export interface IClickHouseQuery {
	name: string;
	legend: string;
	disabled: boolean;
	query: string;
}
export interface IPromQLQuery {
	query: string;
	legend: string;
	disabled: boolean;
	name: string;
}

export interface Query {
	queryType: EQueryType;
	promql: IPromQLQuery[];
	builder: QueryBuilderData;
	clickhouse_sql: IClickHouseQuery[];
	id: string;
	unit?: Format['id'];
}

export type QueryState = Omit<Query, 'queryType'>;

export type BuilderClickHouseResource = Record<string, IClickHouseQuery>;
export type BuilderPromQLResource = Record<string, IPromQLQuery>;
export type BuilderQueryDataResourse = Record<
	string,
	IBuilderQuery | IBuilderFormula | IBuilderTraceOperator
>;

export type MapData =
	| IBuilderQuery
	| IBuilderFormula
	| IBuilderTraceOperator
	| IClickHouseQuery
	| IPromQLQuery;

export type MapQueryDataToApiResult<T> = {
	data: T;
	newLegendMap: Record<string, string>;
};

/** Query Builder and AI share `Query.builder`; the variant says which one it holds. */
export const BuilderVariant = {
	MAIN: 'main',
	AI: 'ai',
} as const;

export type BuilderVariant =
	(typeof BuilderVariant)[keyof typeof BuilderVariant];

/** Builder queries per variant; `Query` has one `builder` slot, so the off-screen variant lives here. */
export type BuildersByVariant = Partial<
	Record<BuilderVariant, Query['builder']>
>;
